/**
 * @file APP 平台策略
 * @description Flutter WebView 环境的平台能力实现
 */

import { bridge, BRIDGE_METHODS } from '@h5-journey/bridge'
import { type UserInfo, useUserStore } from '@h5-journey/stores'
import {
  PlatformType,
  type IPlatformStrategy,
  type NavigateToOptions,
  type LoginOptions,
  type ShareData,
  type LocationResult,
  type LocationNavigateOptions
} from '../types'
import { setRequestFromHeader } from '../request-from'

export function createAppStrategy(): IPlatformStrategy {
  let userStoreRef: ReturnType<typeof useUserStore> | null = null

  return {
    type: PlatformType.APP,

    async onInit({ userStore }) {
      userStoreRef = userStore
      try {
        const res = await bridge.invokeSync<UserInfo>(BRIDGE_METHODS.GET_USER_INFO)
        if (res?.result?.code === 0 && res.result.data) {
          userStore.setUserInfo(res.result.data)
        }
      } catch (error) {
        console.error('[Platform:APP] 获取用户信息失败:', error)
      }

      try {
        const res = await bridge.invokeSync<{
          platform?: string
          version?: string
          deviceId?: string
        }>(BRIDGE_METHODS.GET_VERSION)
        const info = res?.result?.data || {}
        const client = info.platform || 'android'
        const rawVersion = info.version || '6.0.13'
        const version = rawVersion.startsWith('v') ? rawVersion : `v${rawVersion}`
        const cuid = info.deviceId || '39ca90fa-eed9-4505-a877-65ed397d8199'
        setRequestFromHeader({ client, version, cuid })
      } catch (error) {
        console.error('[Platform:APP] 获取设备信息失败:', error)
      }
    },

    navigation: {
      navigateTo(url: string, options: NavigateToOptions = {}) {
        const { title, hasHistory = true, cb, appOptions } = options
        const type = appOptions?.type || 'push'

        console.log('[Platform:APP] navigateTo 原始 URL:', url)
        console.log('[Platform:APP] 当前页面地址:', window.location.href)

        let name = `/web?url=${encodeURIComponent(url)}`
        if (title) name += `&title=${title}`
        if (appOptions?.showClose) name += `&showClose=1`

        console.log('[Platform:APP] 构造的 name 参数:', name)

        if (hasHistory) {
          return bridge
            .invokeSync(BRIDGE_METHODS.OPEN_PAGE, { type, name })
            .then(() => {
              cb?.()
            })
            .catch((error) => {
              console.error('[Platform:APP] 跳转失败:', error)
              throw error
            })
        } else {
          return bridge
            .invokeSync(BRIDGE_METHODS.ROUTER_POP)
            .then(() => bridge.invokeSync(BRIDGE_METHODS.OPEN_PAGE, { type, name }))
            .then(() => {
              cb?.()
            })
            .catch((error) => {
              console.error('[Platform:APP] 跳转失败:', error)
              throw error
            })
        }
      },

      goBack() {
        bridge.invoke(BRIDGE_METHODS.GO_BACK)
      },

      goHome() {
        bridge.invoke(BRIDGE_METHODS.GO_HOME)
      },

      closePage() {
        bridge.invoke(BRIDGE_METHODS.GO_BACK)
      },

      async toLogin(options: LoginOptions = {}) {
        try {
          const res = await bridge.invokeSync<UserInfo>(BRIDGE_METHODS.LOGIN)
          if (res?.result?.code === 0 && res.result.data) {
            userStoreRef?.setUserInfo(res.result.data)
            if (options.onSuccess) {
              options.onSuccess()
            } else {
              window.location.reload()
            }
          }
        } catch (error) {
          console.error('[Platform:APP] 登录失败:', error)
        }
      }
    },

    communication: {
      makePhoneCall(phoneNumber: string) {
        bridge.invokeSync(BRIDGE_METHODS.TEL, phoneNumber)
      },

      async share(data: ShareData) {
        await bridge.invokeSync(BRIDGE_METHODS.SHARE_TRIP, data)
      }
    },

    location: {
      async getLocation(): Promise<LocationResult> {
        try {
          const res: any = await bridge.invokeSync(BRIDGE_METHODS.GET_LOCATION, {})

          if (res.result?.data?.city) {
            const { city, cityId, latitude, longitude } = res.result.data
            return {
              city: city,
              cityId: cityId,
              latitude,
              longitude,
              message: 'success'
            }
          } else {
            throw new Error('[APP] 定位未返回城市信息')
          }
        } catch (e) {
          console.error('[Platform][APP] 定位 bridge 调用失败', e)
          throw new Error('[APP] 定位 bridge 调用失败')
        }
      },
      locationNavigate(options: LocationNavigateOptions): void {
        if (!options.name) {
          console.error('[Platform:APP] 地图导航 name 参数不能为空')
          return
        }
        const params = {
          name: options.name,
          latitude: options.latitude,
          longitude: options.longitude
        }
        bridge.invokeSync(BRIDGE_METHODS.NAVIGATION, params)
      }
    }
  }
}
